import type {
  SessionState,
  TenantBillingAccessState,
  TenantSettings,
} from "@/hooks/useSession";

export interface CurrentUserResponse {
  user: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    role?: string;
    status?: string | null;
  };
  tenant?: {
    id: string;
    accountId?: string;
    name?: string;
    slug?: string;
    plan?: string | null;
    status?: string | null;
    industry?: string | null;
    tenantPackage?: "core" | "insurance" | "farming" | null;
    enabledServices?: string[];
    enabledModules?: string[];
    moduleAccess?: Record<string, boolean>;
    settings?: TenantSettings | null;
    defaultBillingCurrency?: string;
    billingAccess?: TenantBillingAccessState | null;
  };
  permissions: string[];
  provisioningWarning?: boolean;
  provisioningStatus?: string;
  provisioningJobId?: string;
  provisioningErrorDetails?: Array<{
    key: string;
    message: string;
  }>;
  commercialWarnings?: string[];
}

export class UnauthorizedSessionError extends Error {
  constructor(message = "unauthorized") {
    super(message);
    this.name = "UnauthorizedSessionError";
  }
}

export class TransientSessionError extends Error {
  constructor(message = "session_temporarily_unavailable") {
    super(message);
    this.name = "TransientSessionError";
  }
}

const SESSION_REQUEST_TIMEOUT_MS = 10_000;

const parseErrorMessage = async (
  response: Response,
  fallback: string,
): Promise<string> => {
  try {
    const payload = (await response.json()) as {
      error?: string;
      message?: string;
    };
    return payload?.error ?? payload?.message ?? fallback;
  } catch {
    return fallback;
  }
};

export async function fetchCurrentUser(): Promise<SessionState> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SESSION_REQUEST_TIMEOUT_MS);
  let response: Response;

  try {
    response = await fetch("/api/auth/me", {
      credentials: "include",
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new TransientSessionError(
        "Session request timed out. Please retry shortly.",
      );
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  if (response.status === 401) {
    throw new UnauthorizedSessionError();
  }

  if (response.status === 503 || response.status === 502 || response.status === 504) {
    throw new TransientSessionError(
      await parseErrorMessage(
        response,
        "Session service is temporarily unavailable. Please retry shortly.",
      ),
    );
  }

  if (!response.ok) {
    throw new Error(
      await parseErrorMessage(
        response,
        `Session request failed: ${response.status}`,
      ),
    );
  }

  const data = (await response.json()) as CurrentUserResponse;
  return {
    userId: data.user.id,
    role: data.user.role,
    firstName: data.user.firstName,
    lastName: data.user.lastName,
    email: data.user.email,
    userStatus: data.user.status,
    tenantId: data.tenant?.id,
    accountId: data.tenant?.accountId,
    tenantName: data.tenant?.name,
    tenantSlug: data.tenant?.slug,
    tenantPlan: data.tenant?.plan,
    tenantStatus: data.tenant?.status,
    tenantIndustry: data.tenant?.industry,
    tenantPackage: data.tenant?.tenantPackage,
    enabledServices: data.tenant?.enabledServices ?? [],
    enabledModules: data.tenant?.enabledModules ?? data.tenant?.enabledServices ?? [],
    moduleAccess: data.tenant?.moduleAccess ?? {},
    tenantSettings: data.tenant?.settings ?? null,
    defaultBillingCurrency: data.tenant?.defaultBillingCurrency,
    tenantBillingAccess: data.tenant?.billingAccess ?? null,
    permissions: data.permissions ?? [],
  };
}
